import {useCallback, useEffect, useMemo, useState} from "react";
import Chart from "react-apexcharts";

const DATE_AXIS_LEGEND_OFFSET_Y = 34;
const DATE_AXIS_MIN_LABEL_GAP = 68;
const DATE_AXIS_RIGHT_PADDING = 58;

const padDatePart = (value) => String(value).padStart(2, "0");

const getTimestamp = (value) => {
  const timestamp = Number(value);

  if (!Number.isFinite(timestamp)) {
    return null;
  }

  return Math.round(timestamp);
};

const formatDateLabel = (timestamp) => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${padDatePart(date.getDate())}/${padDatePart(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const getUniqueSortedTimestamps = (dateTimestamps = []) => {
  return Array.from(
    new Set(
      dateTimestamps
        .map(getTimestamp)
        .filter((timestamp) => timestamp != null)
    )
  ).sort((left, right) => left - right);
};

const getBoundsFromTimestamps = (timestamps) => {
  if (timestamps.length < 1) {
    return {minX: 0, maxX: 0};
  }

  return {
    minX: timestamps[0],
    maxX: timestamps[timestamps.length - 1],
  };
};

const getMaxNumber = (value, minValue) => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? Math.max(numericValue, minValue) : minValue;
};

const selectVisibleLabels = (items, plotWidth) => {
  if (items.length <= 2) {
    return items;
  }

  const minGap = plotWidth < 480 ? DATE_AXIS_MIN_LABEL_GAP - 8 : DATE_AXIS_MIN_LABEL_GAP;
  const selected = [];

  items.forEach((item) => {
    const previous = selected[selected.length - 1];

    if (!previous || item.x - previous.x >= minGap) {
      selected.push(item);
    }
  });

  const lastItem = items[items.length - 1];
  if (lastItem && !selected.some((item) => item.timestamp === lastItem.timestamp)) {
    while (selected.length > 0) {
      const previous = selected[selected.length - 1];

      if (lastItem.x - previous.x >= minGap) {
        break;
      }

      selected.pop();
    }

    selected.push(lastItem);
  }

  return selected;
};

const buildAxisItems = ({timestamps, minX, maxX, plotWidth}) => {
  if (plotWidth <= 0 || timestamps.length < 1) {
    return [];
  }

  const range = maxX - minX;

  return timestamps
    .filter((timestamp) => timestamp >= minX - 1 && timestamp <= maxX + 1)
    .map((timestamp) => {
      const x = range > 0 ? ((timestamp - minX) / range) * plotWidth : plotWidth / 2;

      return {
        label: formatDateLabel(timestamp),
        timestamp,
        x,
      };
    })
    .filter((item) => Number.isFinite(item.x) && item.x >= -0.5 && item.x <= plotWidth + 0.5);
};

const RealDateAxis = ({plotLeft, plotTop, plotWidth, timestamps, minX, maxX}) => {
  const axisItems = useMemo(
    () => buildAxisItems({timestamps, minX, maxX, plotWidth}),
    [maxX, minX, plotWidth, timestamps]
  );
  const labelItems = useMemo(
    () => selectVisibleLabels(axisItems, plotWidth),
    [axisItems, plotWidth]
  );

  if (plotWidth <= 0 || axisItems.length < 1) {
    return null;
  }

  return (
    <div
      className="ndvi-real-date-axis"
      style={{
        left: `${plotLeft}px`,
        top: `${plotTop + 12}px`,
        width: `${plotWidth}px`,
      }}
      aria-hidden="true"
    >
      {axisItems.map((item) => (
        <span
          key={`tick-${item.timestamp}`}
          className="ndvi-real-date-axis__tick"
          style={{left: `${item.x}px`}}
        />
      ))}
      {labelItems.map((item) => (
        <span
          key={`label-${item.timestamp}`}
          className="ndvi-real-date-axis__label"
          style={{left: `${item.x}px`}}
        >
          {item.label}
        </span>
      ))}
    </div>
  );
};

const RealDateLineChart = ({dateTimestamps = [], options, series, ...chartProps}) => {
  const timestamps = useMemo(() => getUniqueSortedTimestamps(dateTimestamps), [dateTimestamps]);
  const defaultBounds = useMemo(() => getBoundsFromTimestamps(timestamps), [timestamps]);
  const [axisState, setAxisState] = useState({
    maxX: defaultBounds.maxX,
    minX: defaultBounds.minX,
    plotLeft: 0,
    plotTop: 0,
    plotWidth: 0,
  });

  useEffect(() => {
    setAxisState((current) => ({
      ...current,
      maxX: defaultBounds.maxX,
      minX: defaultBounds.minX,
    }));
  }, [defaultBounds.maxX, defaultBounds.minX]);

  const updateAxisState = useCallback(
    (chartContext, bounds = {}) => {
      const readState = () => {
        const globals = chartContext?.w?.globals || {};
        const plotLeft = Number(globals.translateX);
        const plotTop = Number(globals.translateY) + Number(globals.gridHeight);
        const plotWidth = Number(globals.gridWidth);
        const nextMinX = getTimestamp(bounds.min ?? bounds.minX ?? globals.minX) ?? defaultBounds.minX;
        const nextMaxX = getTimestamp(bounds.max ?? bounds.maxX ?? globals.maxX) ?? defaultBounds.maxX;

        setAxisState({
          maxX: nextMaxX,
          minX: nextMinX,
          plotLeft: Number.isFinite(plotLeft) ? plotLeft : 0,
          plotTop: Number.isFinite(plotTop) ? plotTop : 0,
          plotWidth: Number.isFinite(plotWidth) ? plotWidth : 0,
        });
      };

      if (typeof window !== "undefined" && window.requestAnimationFrame) {
        window.requestAnimationFrame(readState);
      } else {
        readState();
      }
    },
    [defaultBounds.maxX, defaultBounds.minX]
  );

  const chartOptions = useMemo(() => {
    const chart = options?.chart || {};
    const grid = options?.grid || {};
    const gridPadding = grid.padding || {};
    const legend = options?.legend || {};
    const originalEvents = chart.events || {};

    return {
      ...options,
      chart: {
        ...chart,
        events: {
          ...originalEvents,
          mounted: (chartContext, config) => {
            originalEvents.mounted?.(chartContext, config);
            updateAxisState(chartContext);
          },
          updated: (chartContext, config) => {
            originalEvents.updated?.(chartContext, config);
            updateAxisState(chartContext);
          },
          zoomed: (chartContext, config) => {
            originalEvents.zoomed?.(chartContext, config);
            updateAxisState(chartContext, config?.xaxis);
          },
          scrolled: (chartContext, config) => {
            originalEvents.scrolled?.(chartContext, config);
            updateAxisState(chartContext, config?.xaxis);
          },
          beforeResetZoom: (chartContext, config) => {
            const result = originalEvents.beforeResetZoom?.(chartContext, config);
            updateAxisState(chartContext, defaultBounds);
            return result;
          },
        },
      },
      grid: {
        ...grid,
        padding: {
          ...gridPadding,
          right: getMaxNumber(gridPadding.right, DATE_AXIS_RIGHT_PADDING),
        },
      },
      legend: {
        ...legend,
        offsetY: getMaxNumber(legend.offsetY, DATE_AXIS_LEGEND_OFFSET_Y),
      },
    };
  }, [defaultBounds, options, updateAxisState]);

  return (
    <div className="ndvi-real-date-chart">
      <Chart
        {...chartProps}
        options={chartOptions}
        series={series}
        type="line"
        width="100%"
      />
      <RealDateAxis
        maxX={axisState.maxX}
        minX={axisState.minX}
        plotLeft={axisState.plotLeft}
        plotTop={axisState.plotTop}
        plotWidth={axisState.plotWidth}
        timestamps={timestamps}
      />
    </div>
  );
};

export default RealDateLineChart;
